import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/use-user-role";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, XCircle, Clock, Mail, Phone, DollarSign, Calendar } from "lucide-react";

interface CoachApplication {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  phone: string | null;
  bio: string;
  experience_years: number | null;
  certifications: string | null;
  specialties: string[] | null;
  hourly_rate: number | null;
  availability: string | null;
  status: string;
  admin_notes: string | null;
  created_at: string;
}

const AdminCoachApplications = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [applications, setApplications] = useState<CoachApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      navigate("/");
    }
  }, [isAdmin, roleLoading, navigate]);

  useEffect(() => {
    fetchApplications();
    
    // Check if there's an application ID in the URL
    const appId = searchParams.get("id");
    if (appId) {
      setSelectedApp(appId);
    }
  }, [searchParams]);

  const fetchApplications = async () => {
    try {
      const { data, error } = await supabase
        .from("tutor_applications")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (applicationId: string, newStatus: string) => {
    setProcessing(true);
    try {
      const updates: any = { status: newStatus };
      if (adminNotes.trim()) {
        updates.admin_notes = adminNotes;
      }

      const { error } = await supabase
        .from("tutor_applications")
        .update(updates)
        .eq("id", applicationId);

      if (error) throw error;

      // If approved, add coach role
      if (newStatus === "approved") {
        const application = applications.find(app => app.id === applicationId);
        if (application) {
          const { error: roleError } = await supabase
            .from("user_roles")
            .insert({ user_id: application.user_id, role: "coach" });

          if (roleError && roleError.code !== "23505") {
            console.error("Error adding coach role:", roleError);
          }
        }
      }

      toast.success(`Application ${newStatus}`);
      setAdminNotes("");
      fetchApplications();
    } catch (error) {
      console.error("Error updating application:", error);
      toast.error("Failed to update application");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: "default" | "secondary" | "destructive" | "outline", icon: any }> = {
      pending: { variant: "outline", icon: Clock },
      approved: { variant: "default", icon: CheckCircle },
      rejected: { variant: "destructive", icon: XCircle },
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const selectedApplication = applications.find(app => app.id === selectedApp);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      <nav className="border-b border-border/40 backdrop-blur-sm bg-background/80 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/admin")} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Admin Portal
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
            Coach Applications
          </h1>
          <p className="text-muted-foreground">Review and manage coach application requests</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Applications List */}
          <div className="lg:col-span-1 space-y-4">
            {applications.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-center text-muted-foreground">No applications yet</p>
                </CardContent>
              </Card>
            ) : (
              applications.map((app) => (
                <Card
                  key={app.id}
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    selectedApp === app.id ? "ring-2 ring-primary" : ""
                  }`}
                  onClick={() => {
                    setSelectedApp(app.id);
                    setAdminNotes(app.admin_notes || "");
                  }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{app.full_name}</CardTitle>
                        <CardDescription className="text-sm">{app.email}</CardDescription>
                      </div>
                      {getStatusBadge(app.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground">
                      Applied {new Date(app.created_at).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Application Details */}
          <div className="lg:col-span-2">
            {selectedApplication ? (
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-2xl">{selectedApplication.full_name}</CardTitle>
                      <CardDescription>Application Details</CardDescription>
                    </div>
                    {getStatusBadge(selectedApplication.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Contact Information */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedApplication.email}</span>
                      </div>
                      {selectedApplication.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedApplication.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Professional Details */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Professional Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedApplication.experience_years && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Experience</p>
                          <p className="text-sm font-medium">{selectedApplication.experience_years} years</p>
                        </div>
                      )}
                      {selectedApplication.hourly_rate && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Desired Rate</p>
                            <p className="text-sm font-medium">${selectedApplication.hourly_rate}/hour</p>
                          </div>
                        </div>
                      )}
                      {selectedApplication.availability && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Availability</p>
                            <p className="text-sm font-medium">{selectedApplication.availability}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Certifications */}
                  {selectedApplication.certifications && (
                    <div className="space-y-2">
                      <h3 className="font-semibold text-lg">Certifications</h3>
                      <p className="text-sm">{selectedApplication.certifications}</p>
                    </div>
                  )}

                  {/* Specialties */}
                  {selectedApplication.specialties && selectedApplication.specialties.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-semibold text-lg">Specialties</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedApplication.specialties.map((specialty, index) => (
                          <Badge key={index} variant="secondary">
                            {specialty}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Bio */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-lg">Bio</h3>
                    <p className="text-sm whitespace-pre-wrap">{selectedApplication.bio}</p>
                  </div>

                  {/* Admin Notes */}
                  <div className="space-y-2">
                    <Label htmlFor="admin-notes">Admin Notes</Label>
                    <Textarea
                      id="admin-notes"
                      placeholder="Add notes about this application..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={3}
                    />
                  </div>

                  {/* Actions */}
                  {selectedApplication.status === "pending" && (
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="default"
                        className="flex-1"
                        onClick={() => handleUpdateStatus(selectedApplication.id, "approved")}
                        disabled={processing}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleUpdateStatus(selectedApplication.id, "rejected")}
                        disabled={processing}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}

                  {selectedApplication.status !== "pending" && adminNotes !== selectedApplication.admin_notes && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => handleUpdateStatus(selectedApplication.id, selectedApplication.status)}
                      disabled={processing}
                    >
                      Save Notes
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-12 pb-12 text-center">
                  <p className="text-muted-foreground">Select an application to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCoachApplications;
